export const LIST_GROUPING_STORAGE_KEY = "unibox_list_grouping";
export const LIST_DETAIL_STORAGE_KEY = "unibox_list_detail";

export function readListGrouping() {
  try {
    const stored = localStorage.getItem(LIST_GROUPING_STORAGE_KEY);
    if (stored === "conversation" || stored === "account") return stored;
    return "account";
  } catch {
    return "account";
  }
}

/** "full" = asunto/preview + chip de cuenta; "compact" = solo línea principal */
export function readListDetail() {
  try {
    const stored = localStorage.getItem(LIST_DETAIL_STORAGE_KEY);
    if (stored === "full" || stored === "compact") return stored;
    return "full";
  } catch {
    return "full";
  }
}

export function normalizeEmail(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";

  const bracketMatch = raw.match(/<([^>]+)>/);
  if (bracketMatch?.[1]) return bracketMatch[1].trim();

  const firstItem = raw.split(",")[0]?.trim() || "";
  const emailMatch = firstItem.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  return (emailMatch?.[0] || firstItem).toLowerCase();
}

export function parseAllEmails(...fields) {
  const set = new Set();
  for (const field of fields) {
    const raw = String(field || "");
    for (const m of raw.matchAll(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi)) {
      set.add(m[0].toLowerCase());
    }
  }
  return [...set];
}

export function getEmailDirection(email) {
  if (!email) return "received";
  if (email.direction === "sent" || email.direction === "received") {
    return email.direction;
  }
  const account = normalizeEmail(email.accountEmail);
  const from = normalizeEmail(email.from);
  const fromSentFolder = String(email.gmailId || "").includes(":sent:");
  return fromSentFolder || (from && from === account) ? "sent" : "received";
}

/** Dirección Para al responder (no usar getEmailContact: devuelve claves de hilo). */
export function getReplyToAddress(email, accounts = []) {
  if (!email) return "";

  const account = normalizeEmail(email.accountEmail);
  const fromNorm = normalizeEmail(email.from);
  const direction = getEmailDirection(email);

  if (direction === "received") {
    const rawFrom = String(email.from || "").trim();
    if (rawFrom && rawFrom !== "yo-mismo" && rawFrom.includes("@")) {
      return rawFrom;
    }
    if (fromNorm && fromNorm !== account) return fromNorm;
    const external = parseAllEmails(email.to_email, email.cc_email).filter(
      (r) => r !== account
    );
    return external[0] || fromNorm || "";
  }

  const recipients = parseAllEmails(email.to_email, email.cc_email).filter(
    (r) => r !== account
  );
  const rawTo = String(email.to_email || "").trim();
  if (recipients.length === 1 && rawTo.includes("@")) return rawTo;
  return recipients[0] || rawTo || "";
}

export function getEmailContact(email) {
  const account = normalizeEmail(email?.accountEmail);
  const from = normalizeEmail(email?.from);
  const direction = getEmailDirection(email);

  if (direction === "received") {
    if (
      from &&
      account &&
      from === account &&
      parseAllEmails(email?.to_email, email?.cc_email).length <= 1
    ) {
      return "yo-mismo";
    }
    if (from) return from;
    return "desconocido";
  }

  const recipients = parseAllEmails(email?.to_email, email?.cc_email);
  if (!recipients.length) return from === account ? "yo-mismo" : "enviado";

  const first =
    recipients.find((r) => r !== account) ||
    recipients.find((r) => r === account) ||
    recipients[0];
  if (first === account) return "yo-mismo";
  return first;
}

const GENERIC_MAILBOX_LOCALS = new Set([
  "info",
  "noreply",
  "no-reply",
  "donotreply",
  "hello",
  "mail",
  "notifications",
  "notification",
  "announce",
  "support",
  "contact",
  "billing",
  "receipts",
]);

/** Buzones compartidos / reenvíos "via …" → un hilo por conversación, no uno por contacto. */
export function shouldSplitContactThreads(contact, email) {
  if (!contact || contact === "yo-mismo" || contact === "enviado") return false;
  const norm = normalizeEmail(contact);
  if (!norm.includes("@")) return false;

  const local = norm.split("@")[0] || "";
  if (GENERIC_MAILBOX_LOCALS.has(local)) return true;
  if (local.startsWith("noreply") || local.includes("notification")) return true;

  const fromRaw = String(email?.from || "").toLowerCase();
  if (fromRaw.includes(" via ")) return true;

  return false;
}

/** Cuenta desde la que debe salir la respuesta (la que recibió el mensaje). */
export function getReplyFromEmail(email, accounts = []) {
  const fromAccount = normalizeEmail(email?.accountEmail);
  if (fromAccount) return fromAccount;

  const byId = (accounts || []).find(
    (a) => Number(a.id) === Number(email?.accountId)
  );
  if (byId?.email) return normalizeEmail(byId.email);

  return normalizeEmail(accounts[0]?.email);
}

export function parseThreadKey(threadKey) {
  const str = String(threadKey || "");
  const parts = str.split("::");

  if (parts.length >= 3) {
    if (parts[1] === "c") {
      const contact = parts[2] || "";
      const threadBucket =
        parts.length > 3 ? parts.slice(3).join("::") : null;
      return {
        mode: "account",
        account: parts[0],
        contact,
        threadBucket,
        threadId: threadBucket,
      };
    }
    if (parts[1] === "t") {
      return {
        mode: "conversation",
        account: parts[0],
        contact: null,
        threadId: parts.slice(2).join("::"),
      };
    }
  }

  const idx = str.indexOf("::");
  if (idx === -1) {
    return { mode: "account", account: null, contact: str, threadId: null };
  }

  return {
    mode: "account",
    account: str.slice(0, idx),
    contact: str.slice(idx + 2),
    threadId: null,
  };
}

export function buildGroupedThreads(rawThreads, listGrouping = "account") {
  const result = {};
  const seenByKey = new Map();

  Object.values(rawThreads || {}).forEach((emails) => {
    (emails || []).forEach((email) => {
      const account = normalizeEmail(email.accountEmail);
      if (!account) return;

      const contact = getEmailContact(email);
      if (!contact) return;

      const gmailId = String(email.gmailId || "").trim();
      if (!gmailId) return;

      let key;
      if (listGrouping === "conversation") {
        const threadId = String(email.threadId || email.gmailId || "").trim();
        if (!threadId) return;
        key = `${account}::t::${threadId}`;
      } else if (shouldSplitContactThreads(contact, email)) {
        const threadId = String(email.threadId || "").trim();
        const bucket = threadId || gmailId;
        key = `${account}::c::${contact}::${bucket}`;
      } else {
        key = `${account}::c::${contact}`;
      }

      if (!seenByKey.has(key)) seenByKey.set(key, new Set());
      const seen = seenByKey.get(key);
      if (seen.has(gmailId)) return;
      seen.add(gmailId);

      if (!result[key]) result[key] = [];
      result[key].push(email);
    });
  });

  return result;
}

/** Ordena hilos por fecha del último mensaje (misma lógica que la lista izquierda). */
export function getSortedConversationThreadKeys(groupedThreads) {
  const entries = Object.entries(groupedThreads || {}).filter(
    ([, emails]) => Array.isArray(emails) && emails.length > 0
  );

  entries.sort((a, b) => {
    const latest = (list) =>
      Math.max(
        0,
        ...(list || []).map((e) => new Date(e.date || 0).getTime())
      );
    return latest(b[1]) - latest(a[1]);
  });

  return entries.map(([key]) => key);
}

export function isNearDuplicateEmail(a, b) {
  if (String(a?.subject || "").trim() !== String(b?.subject || "").trim()) {
    return false;
  }
  if (normalizeEmail(a?.from) !== normalizeEmail(b?.from)) return false;
  const ta = new Date(a?.date || 0).getTime();
  const tb = new Date(b?.date || 0).getTime();
  if (!ta || !tb) return false;
  return Math.abs(ta - tb) <= 2 * 60 * 1000;
}

export function collectDeleteTargets(threadEmails, gmailId) {
  const id = String(gmailId || "").trim();
  if (!id) return [];
  const list = Array.isArray(threadEmails) ? threadEmails : [];
  const target = list.find((e) => String(e?.gmailId || "").trim() === id);
  if (!target) return [id];
  const ids = list
    .filter((e) => {
      const eid = String(e?.gmailId || "").trim();
      return eid === id || isNearDuplicateEmail(e, target);
    })
    .map((e) => String(e.gmailId).trim())
    .filter(Boolean);
  return [...new Set(ids.length ? ids : [id])];
}

export function dedupeEmailsByGmailId(emails) {
  const byId = new Map();
  for (const email of emails || []) {
    const id = String(email?.gmailId || "").trim();
    if (!id) continue;
    if (!byId.has(id)) byId.set(id, email);
  }

  const list = [...byId.values()];
  const out = [];

  const score = (e) => {
    let s = String(e?.snippet || "").trim().length;
    const gid = String(e?.gmailId || "");
    if (gid.startsWith("imap:")) s += 500;
    if (!gid.startsWith("local:sent:")) s += 100;
    return s;
  };

  for (const email of list) {
    const dupIdx = out.findIndex((e) => isNearDuplicateEmail(e, email));
    if (dupIdx === -1) {
      out.push(email);
      continue;
    }
    if (score(email) > score(out[dupIdx])) {
      out[dupIdx] = email;
    }
  }

  return out;
}
